import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { FloppyDisk, DownloadSimple, ArrowsClockwise } from "@phosphor-icons/react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MONTHS_ES, money, num } from "./faUtils";

// Campos de captura por escenario, agrupados como el spec del CFO
const GROUPS = [
  { title: "Ingresos", fields: [
    ["ingresos_adicionales", "Ingresos adicionales ($)"],
    ["bonos", "Bonos cumplimiento ($)"],
    ["penalizaciones", "Penalizaciones ($)"],
  ]},
  { title: "Costos variables directos", fields: [
    ["mano_obra_base", "Mano obra · sueldo base ($/mes)"],
    ["mano_obra_destajo", "Mano obra · destajos ($)"],
    ["explosivos", "Explosivos ($)"],
    ["aceros", "Aceros perforación ($)"],
    ["diesel", "Diésel ($)"],
    ["mantenimiento", "Mantenimiento ($)"],
    ["otros_directos", "Otros directos ($)"],
  ]},
  { title: "Costos fijos", fields: [
    ["indirectos_obra", "Indirectos de obra ($)"],
    ["administracion", "Administración ($)"],
  ]},
  { title: "Capital de trabajo y otros", fields: [
    ["capex_extra", "CAPEX adicional del mes ($)"],
    ["dso", "DSO · días cobro cliente"],
    ["dpo", "DPO · días pago proveedores"],
    ["inv_dias", "Inventario en mina (días)"],
    ["turnos", "Turnos trabajados"],
  ]},
];

const INDEX_FIELDS = [["inpc", "INPC"], ["diesel", "Diésel ($/lt)"], ["acero", "Acero (índice)"], ["usd", "USD (TC)"]];

const clean = (v) => (v === "" || v === null || v === undefined ? "" : v);

const mlTotalOf = (doc, sc) => Object.values((doc[sc] || {}).ml_frentes || {}).reduce((s, v) => s + Number(v || 0), 0);

function ScenarioCol({ sc, title, tone, doc, frentes, setField, setMl, setIdx }) {
  return (
    <div className={`rounded-lg border p-4 space-y-4 ${tone}`}>
      <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-600">{title}</p>
      {/* ML por frente */}
      <div>
        <p className="text-[10px] uppercase font-bold text-slate-500 mb-1.5">
          ML por frente — total <span className="text-slate-900">{num(mlTotalOf(doc, sc))} ML</span>
        </p>
        {frentes.length === 0 && <p className="text-[11px] italic text-slate-400">Crea frentes en Parámetros.</p>}
        <div className="space-y-1.5">
          {frentes.map((f) => (
            <div key={f.id} className="grid grid-cols-[1fr_100px] gap-2 items-center">
              <span className="text-xs text-slate-600 truncate">{f.name} <span className="text-slate-400">({money(f.precio_ml)}/ML)</span></span>
              <Input type="number" step="any" className="h-8 text-xs text-right"
                value={clean(((doc[sc] || {}).ml_frentes || {})[f.id])}
                onChange={(e) => setMl(sc, f.id, e.target.value)}
                data-testid={`fa-cap-${sc}-ml-${f.id}`} />
            </div>
          ))}
          {sc === "real" && ((doc.real || {}).ml_frentes || {})._unassigned > 0 && (
            <p className="text-[10px] text-amber-700">+ {num(doc.real.ml_frentes._unassigned)} ML sin frente asignado (sync)</p>
          )}
        </div>
      </div>
      {GROUPS.map((g) => (
        <div key={g.title}>
          <p className="text-[10px] uppercase font-bold text-slate-500 mb-1.5">{g.title}</p>
          <div className="grid grid-cols-2 gap-2">
            {g.fields.map(([k, l]) => (
              <div key={k}>
                <Label className="text-[9px] uppercase font-bold text-slate-400 leading-tight block">{l}</Label>
                <Input type="number" step="any" className="h-8 text-xs text-right"
                  value={clean((doc[sc] || {})[k])}
                  onChange={(e) => setField(sc, k, e.target.value)}
                  data-testid={`fa-cap-${sc}-${k}`} />
              </div>
            ))}
          </div>
        </div>
      ))}
      {sc === "real" && (
        <div>
          <p className="text-[10px] uppercase font-bold text-slate-500 mb-1.5">Índices del mes (escalatoria)</p>
          <div className="grid grid-cols-4 gap-2">
            {INDEX_FIELDS.map(([k, l]) => (
              <div key={k}>
                <Label className="text-[9px] uppercase font-bold text-slate-400">{l}</Label>
                <Input type="number" step="any" className="h-8 text-xs text-right"
                  value={clean(((doc.real || {}).indices || {})[k])}
                  onChange={(e) => setIdx(k, e.target.value)}
                  data-testid={`fa-cap-idx-${k}`} />
              </div>
            ))}
          </div>
          <p className="text-[10px] text-slate-400 mt-1">Se comparan contra los índices base del contrato (Parámetros) con los pesos configurados.</p>
        </div>
      )}
    </div>
  );
}

export default function FaCapture({ year, report, onSaved, month: monthProp, onMonthChange }) {
  // Iter117: mes controlado desde el padre (selector global). Si no viene
  // por prop, mantenemos estado local para retrocompatibilidad.
  const [monthLocal, setMonthLocal] = useState(new Date().getMonth() + 1);
  const month = monthProp ?? monthLocal;
  const setMonth = onMonthChange ?? setMonthLocal;
  const [doc, setDoc] = useState(null);
  const [busy, setBusy] = useState(false);
  const frentes = useMemo(() => (report.frentes || []).filter((f) => f.active !== false), [report]);

  useEffect(() => {
    api.get(`/finanalysis/months/${year}`)
      .then((r) => setDoc(r.data[month - 1]))
      .catch((e) => toast.error(apiError(e)));
  }, [year, month]);

  if (!doc) return <div className="py-16 text-center text-slate-400 text-sm">Cargando mes…</div>;

  const setField = (scenario, key, value) =>
    setDoc((d) => ({ ...d, [scenario]: { ...(d[scenario] || {}), [key]: value } }));
  const setMl = (scenario, fid, value) =>
    setDoc((d) => ({
      ...d,
      [scenario]: { ...(d[scenario] || {}), ml_frentes: { ...((d[scenario] || {}).ml_frentes || {}), [fid]: value } },
    }));
  const setIdx = (key, value) =>
    setDoc((d) => ({ ...d, real: { ...(d.real || {}), indices: { ...((d.real || {}).indices || {}), [key]: value } } }));

  const numify = (obj) => {
    const out = {};
    for (const [k, v] of Object.entries(obj || {})) {
      if (k === "ml_frentes" || k === "indices") {
        out[k] = Object.fromEntries(Object.entries(v || {}).map(([kk, vv]) => [kk, Number(vv || 0)]));
      } else out[k] = Number(v || 0);
    }
    return out;
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/finanalysis/months/${year}/${month}`, {
        budget: numify(doc.budget), real: numify(doc.real),
      });
      toast.success(`${MONTHS_ES[month - 1]} ${year} guardado`);
      onSaved?.();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const sync = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/finanalysis/sync/${year}`);
      const d = r.data;
      toast.success(`Sincronizado · meses actualizados: ${d.months_updated.join(", ") || "ninguno"} · nómina base ${money(d.nomina_base_mensual)}`,
        { duration: 6000 });
      if (Object.keys(d.unmatched_areas || {}).length > 0) {
        toast.warning(`Áreas sin frente asignado: ${Object.keys(d.unmatched_areas).join(", ")}. ${d.hint}`, { duration: 9000 });
      }
      const months = await api.get(`/finanalysis/months/${year}`);
      setDoc(months.data[month - 1]);
      onSaved?.();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4 pt-3">
      <div className="flex flex-wrap items-center gap-2.5">
        <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
          <SelectTrigger className="w-40" data-testid="fa-cap-month-select"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white">
            {MONTHS_ES.map((l, i) => <SelectItem key={i} value={String(i + 1)}>{l} {year}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={sync} disabled={busy} className="gap-1.5" data-testid="fa-cap-sync-btn">
          <ArrowsClockwise size={14} weight="bold" /> Sincronizar reales (destajos + horómetros + nómina)
        </Button>
        <Button size="sm" onClick={save} disabled={busy} className="gap-1.5 bg-blue-900 hover:bg-blue-800 text-white ml-auto" data-testid="fa-cap-save-btn">
          <FloppyDisk size={14} weight="bold" /> {busy ? "Guardando…" : `Guardar ${MONTHS_ES[month - 1]}`}
        </Button>
      </div>
      <div className="grid lg:grid-cols-2 gap-4">
        <ScenarioCol sc="budget" title={`Budget · ${MONTHS_ES[month - 1]} ${year}`} tone="bg-blue-50/40 border-blue-200"
          doc={doc} frentes={frentes} setField={setField} setMl={setMl} setIdx={setIdx} />
        <ScenarioCol sc="real" title={`Real · ${MONTHS_ES[month - 1]} ${year}`} tone="bg-white border-slate-200"
          doc={doc} frentes={frentes} setField={setField} setMl={setMl} setIdx={setIdx} />
      </div>
    </div>
  );
}
